<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { RequiredKeys } from '@/utils/types';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed } from 'vue';

import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import JobIdAndIconLinkUnit from '@/components/units/JobIdAndIconLinkUnit.vue';
import DetailedJobBlockWrapper from '@/components/wrappers/DetailedJobBlockWrapper.vue';
import * as dateHelpers from '@/helpers/date';
import * as commonConstants from '@/constants/common';

export interface Props {
  ticket?: ReadonlyDeep<
    RequiredKeys<
      Partial<
        Pick<
          openapi.GetZammadTickets_200,
          'created_by' | 'created_at' | 'updated_by' | 'updated_at' | 'rh_job'
        >
      >
    >
  >;
}
const props = withDefaults(defineProps<Props>(), {
  ticket: undefined,
});

const ticket = computed<Partial<NonNullable<Props['ticket']>>>(
  () => props.ticket || {},
);

const createdAt = computed(() => {
  if (!props.ticket?.created_at) return;
  return dateHelpers.getTicketDetailsFormatDateTime(props.ticket.created_at);
});

const updatedAt = computed(() => {
  if (!props.ticket?.updated_at) return;
  return dateHelpers.getTicketDetailsFormatDateTime(props.ticket.updated_at);
});

// TODO jobDate
const affiliatedJobs = computed(() => {
  return props.ticket?.rh_job
    ?.split(',')
    .filter((jobId) => jobId.trim())
    .map((jobId) => Number(jobId))
    .filter(Number.isFinite);
});
</script>

<!-- TODO similar to @/components/descLists/DetailedJobBaseInfoDescList.vue -->
<template>
  <dl
    :class="$style.zammadTicketDetailsDescList"
    data-testid="zammadTicketDetailsDescList"
  >
    <DetailedJobBlockWrapper :class="$style.item">
      <dt :class="$style.dt">
        <div :class="$style.iconWrapper">
          <v-icon :class="$style.icon" icon="mdi-account-outline"></v-icon>
        </div>
        Created by
      </dt>
      <dd :class="$style.dd">
        <template v-if="ticket.created_by">
          {{ ticket.created_by }}
        </template>
        <template v-else>{{ commonConstants.EM_DASH }}</template>
      </dd>
    </DetailedJobBlockWrapper>
    <DetailedJobBlockWrapper :class="$style.item">
      <dt :class="$style.dt">
        <div :class="$style.iconWrapper">
          <v-icon :class="$style.icon" icon="mdi-calendar"></v-icon>
        </div>
        Created
      </dt>
      <dd :class="$style.dd">
        <template v-if="ticket.created_at">
          <KParentTooltip location="bottom">{{
            ticket.created_at
          }}</KParentTooltip>
          {{ createdAt }}
        </template>
        <template v-else>{{ commonConstants.EM_DASH }}</template>
      </dd>
    </DetailedJobBlockWrapper>
    <DetailedJobBlockWrapper :class="$style.item">
      <dt :class="$style.dt">
        <div :class="$style.iconWrapper">
          <v-icon :class="$style.icon" icon="mdi-account-outline"></v-icon>
        </div>
        Updated by
      </dt>
      <dd :class="$style.dd">
        <template v-if="ticket.updated_by">
          {{ ticket.updated_by }}
        </template>
        <template v-else>{{ commonConstants.EM_DASH }}</template>
      </dd>
    </DetailedJobBlockWrapper>
    <DetailedJobBlockWrapper :class="$style.item">
      <dt :class="$style.dt">
        <div :class="$style.iconWrapper">
          <v-icon :class="$style.icon" icon="mdi-calendar"></v-icon>
        </div>
        Last updated
      </dt>
      <dd :class="$style.dd">
        <template v-if="ticket.updated_at">
          <KParentTooltip location="bottom">{{
            ticket.updated_at
          }}</KParentTooltip>
          {{ updatedAt }}
        </template>
        <template v-else>{{ commonConstants.EM_DASH }}</template>
      </dd>
    </DetailedJobBlockWrapper>
    <DetailedJobBlockWrapper :class="$style.item">
      <dt :class="$style.dt">
        <div :class="$style.iconWrapper">
          <v-icon :class="$style.icon" icon="mdi-link"></v-icon>
        </div>
        Affiliated job(s)
      </dt>
      <dd :class="$style.dd">
        <template v-if="affiliatedJobs?.length">
          <template v-for="jobId in affiliatedJobs" :key="jobId">
            <JobIdAndIconLinkUnit :job-id="jobId" />
            {{ ' ' }}
          </template>
        </template>
        <template v-else>{{ commonConstants.EM_DASH }}</template>
      </dd>
    </DetailedJobBlockWrapper>
  </dl>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.zammadTicketDetailsDescList {
  margin: 0;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  gap: 8px;
}
    .iconWrapper {
      background-color: colors.$color-gray-100;
      color: colors.$color-text-primary;
      font-size: 11px;
      line-height: 1;
      padding: 2px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .item {
      padding: 8px 12px;
    }
      .dt {
        font-size: 16px;
        font-weight: 500;
        color: colors.$color-text-tertiary;
        display: flex;
        gap: 8px;
      }
      .dd {
        margin: 2px 0 0 0;
        font-size: 16px;
        font-weight: 500;
        color: colors.$color-text-primary;
      }
</style>
